import { useState, useEffect, useMemo, useRef } from 'react';
import { EventSeries } from '../types/eventSeries.types';
import { locationCache } from '../services/LocationCacheService';
import { supabaseEventSeriesService } from '../supabase/eventSeries.service';
import { computeSeriesEndDate, computeSeriesPattern } from '../utils/seriesPatternUtils';

interface SeriesPatternData {
  pattern: string | null;
  loading: boolean;
  daySchedules?: Record<string, { start: string; end: string }> | null;
}

/**
 * 🚀 BATCH LOADING des séries - Version optimisée pour la HomePage touriste
 *
 * Au lieu de 24 appels individuels useSmartSeries (avec 24 consumers + 3-4 channels Realtime),
 * ce hook charge toutes les séries en une seule passe depuis IndexedDB + fallback Supabase batch.
 * Aucun abonnement Realtime (inutile côté touriste).
 *
 * @param seriesIds - Liste des seriesId à charger
 * @param seriesEventsMap - Map<seriesId, Event[]> des occurrences déjà chargées (pour fallback endDate)
 * @returns Map<seriesId, { pattern, loading }>
 */
export const useSeriesBatch = (
  seriesIds: string[],
  seriesEventsMap?: Map<string, any[]>
): Map<string, SeriesPatternData> => {
  const [seriesMap, setSeriesMap] = useState<Map<string, EventSeries>>(new Map());
  const [loading, setLoading] = useState(true);

  // Stabiliser la liste d'IDs pour éviter les re-renders inutiles
  const stableIdsRef = useRef<string>('');
  const currentIdsKey = seriesIds.filter(Boolean).sort().join(',');

  useEffect(() => {
    // Éviter le rechargement si la liste n'a pas changé
    if (stableIdsRef.current === currentIdsKey) return;
    stableIdsRef.current = currentIdsKey;

    const uniqueIds = Array.from(new Set(seriesIds.filter(Boolean)));
    if (uniqueIds.length === 0) {
      setSeriesMap(new Map());
      setLoading(false);
      return;
    }

    const loadAll = async () => {
      setLoading(true);

      const result = new Map<string, EventSeries>();
      const cacheMissIds: string[] = [];

      // 1. Batch load depuis IndexedDB
      await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const cached = await locationCache.getSeries(id);
            if (cached) {
              const normalized: EventSeries = {
                ...cached,
                createdAt: cached.createdAt instanceof Date ? cached.createdAt : new Date(cached.createdAt),
                updatedAt: cached.updatedAt instanceof Date ? cached.updatedAt : new Date(cached.updatedAt),
              };
              result.set(id, normalized);
            } else {
              cacheMissIds.push(id);
            }
          } catch {
            cacheMissIds.push(id);
          }
        })
      );

      // 2. Fallback Supabase pour les cache miss (1 seule requête batch)
      if (cacheMissIds.length > 0) {
        try {
          const fromSupabase = await supabaseEventSeriesService.getSeriesByIds(cacheMissIds);
          for (const series of fromSupabase) {
            result.set(series.id, series);
            // Sauvegarder en cache pour les prochains accès
            await locationCache.saveSeries(series);
          }
        } catch (err) {
          console.error('❌ [useSeriesBatch] Erreur fallback Supabase:', err);
        }
      }

      setSeriesMap(result);
      setLoading(false);
    };

    loadAll();
  }, [currentIdsKey, seriesIds]);

  // Calculer les patterns pour toutes les séries (une seule passe memo)
  const patternsMap = useMemo(() => {
    const map = new Map<string, SeriesPatternData>();

    for (const id of seriesIds.filter(Boolean)) {
      const series = seriesMap.get(id);
      if (series) {
        // Calculer endDate depuis la config série
        let endDate = computeSeriesEndDate(series);

        // Fallback: si pas de endDate calculable, prendre la dernière occurrence réelle
        if (!endDate && seriesEventsMap) {
          const events = seriesEventsMap.get(id);
          if (events && events.length > 0) {
            const lastEvent = events
              .filter((e: any) => e.datetime)
              .sort((a: any, b: any) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime())[0];
            if (lastEvent?.datetime) {
              endDate = new Date(lastEvent.datetime);
            }
          }
        }

        const pattern = computeSeriesPattern(series, endDate);
        map.set(id, { pattern, loading: false, daySchedules: series.daySchedules });
      } else {
        map.set(id, { pattern: null, loading });
      }
    }

    return map;
  }, [seriesMap, loading, seriesIds, seriesEventsMap]);

  return patternsMap;
};
